// src/components/FirestoreDateInput.js
import React, { forwardRef } from 'react';
import { DateInput, useInput } from 'react-admin';
import { Timestamp } from 'firebase/firestore';

/**
 * Custom DateInput component that handles Firestore Timestamp objects
 * This component converts Firestore Timestamps to JavaScript Date objects for the input
 * and converts back to Timestamps when saving
 */
const FirestoreDateInput = forwardRef((props, ref) => {
    const {
        field,
        fieldState,
        formState,
        isRequired,
        source,
    } = useInput({
        ...props,
        format: (value) => {
            // Convert Firestore Timestamp to Date for the input
            if (!value) return null;
            
            try {
                // Handle Firestore Timestamp
                if (value instanceof Timestamp || 
                    (typeof value === 'object' && value.seconds && value.nanoseconds)) {
                    return new Date(value.seconds * 1000);
                }
                
                // Handle Firestore toDate() method
                if (typeof value === 'object' && typeof value.toDate === 'function') {
                    return value.toDate();
                }
                
                // Handle ISO string
                if (typeof value === 'string') {
                    return new Date(value);
                }
                
                // Handle numeric timestamp (milliseconds)
                if (typeof value === 'number') {
                    return new Date(value);
                }
                
                // Already a Date object
                if (value instanceof Date) {
                    return value;
                }
                
                console.warn('Unknown date format in FirestoreDateInput:', value);
                return null;
            } catch (error) {
                console.error('Error formatting date in FirestoreDateInput:', error, value);
                return null;
            }
        },
        parse: (value) => {
            // Keep as a Date object - Firestore will convert it to a Timestamp
            return value;
        },
    });

    return (
        <DateInput
            {...props}
            source={source}
            label={props.label}
            isRequired={isRequired}
            helperText={props.helperText}
            {...field}
            meta={{ error: fieldState.error, touched: fieldState.isTouched }}
            ref={ref}
        />
    );
});

export default FirestoreDateInput;